import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { servicesData } from '../../mocks/services';
import { useCart } from '../../context/CartContext';

export default function PricingPage() {
  const { addToCart } = useCart();

  const handleAddToCart = (serviceId: string) => {
    const service = servicesData.find(s => s.id === serviceId);
    if (service) {
      addToCart(service);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Banner */}
      <section className="relative h-[400px] flex items-center justify-center overflow-hidden mt-20">
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary-light to-accent"></div>

        <div className="relative z-10 text-center text-white max-w-3xl mx-auto px-8">
          <h1 className="font-serif text-5xl mb-5">Transparent Pricing</h1>
          <p className="text-lg text-white/90">
            Quality dental care at fair, competitive prices
          </p>
        </div>
      </section>

      {/* Pricing Table */}
      <section className="py-20 px-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white border-2 border-sand rounded-xl overflow-hidden shadow-sm">
            <div className="grid grid-cols-3 bg-primary text-white p-5 font-semibold">
              <div>Service</div>
              <div>Description</div>
              <div className="text-right">Price</div>
            </div>
            {servicesData.map((service, index) => (
              <div
                key={service.id}
                className={`grid grid-cols-3 p-5 items-center ${
                  index % 2 === 0 ? 'bg-white' : 'bg-sand/30'
                } hover:bg-sand/50 transition-colors`}
              >
                <div className="font-medium text-navy">{service.name}</div>
                <div className="text-navy/60 text-sm">{service.description}</div>
                <div className="text-right">
                  <div className="flex items-center justify-end gap-3">
                    <span className="font-serif text-2xl text-primary">${service.price}</span>
                    <button
                      onClick={() => handleAddToCart(service.id)}
                      className="px-5 py-2 bg-accent text-white rounded-full text-sm font-medium hover:bg-accent-dark transition-all cursor-pointer whitespace-nowrap"
                    >
                      Add
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Insurance & Payment Options */}
      <section className="py-20 px-8">
        <div className="max-w-7xl mx-auto">
          <div className="mt-12 bg-sand/30 rounded-xl p-8">
            <h3 className="font-serif text-2xl text-navy mb-4">Insurance &amp; Payment Options</h3>
            <div className="grid grid-cols-2 gap-8 text-navy/70">
              <div>
                <h4 className="font-semibold text-navy mb-3">We Accept Most Insurance</h4>
                <p className="leading-relaxed mb-4">
                  We work with most major dental insurance providers. Our team will help you maximize your benefits.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-navy mb-3">Flexible Payment Plans</h4>
                <p className="leading-relaxed mb-4">
                  We offer flexible payment options to make quality dental care accessible for everyone.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-8 bg-sand/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-serif text-4xl text-navy mb-4">Get In Touch</h2>
            <p className="text-navy/60 max-w-2xl mx-auto">
              Have questions about our pricing or services? We're here to help you find the best dental care solution.
            </p>
          </div>

          <div className="grid grid-cols-3 gap-8">
            <a
              href="tel:2296519254"
              className="bg-white rounded-xl p-8 text-center hover:shadow-lg transition-all cursor-pointer group"
            >
              <div className="w-16 h-16 flex items-center justify-center bg-primary/10 rounded-full mx-auto mb-5 group-hover:bg-primary/20 transition-colors">
                <i className="ri-phone-line text-3xl text-primary"></i>
              </div>
              <h3 className="font-serif text-xl text-navy mb-2">Call Us</h3>
              <p className="text-accent font-medium text-lg">(229) 651-9254</p>
              <p className="text-navy/60 text-sm mt-2">Mon-Fri: 8am - 6pm</p>
            </a>

            <a
              href="mailto:info@bfdental.org"
              className="bg-white rounded-xl p-8 text-center hover:shadow-lg transition-all cursor-pointer group"
            >
              <div className="w-16 h-16 flex items-center justify-center bg-primary/10 rounded-full mx-auto mb-5 group-hover:bg-primary/20 transition-colors">
                <i className="ri-mail-line text-3xl text-primary"></i>
              </div>
              <h3 className="font-serif text-xl text-navy mb-2">Email Us</h3>
              <p className="text-accent font-medium">info@bfdental.org</p>
              <p className="text-navy/60 text-sm mt-2">We'll respond within 24 hours</p>
            </a>

            <a
              href="https://www.google.com/maps/search/?api=1&query=825+Simms+St+Golden+CO+80401"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white rounded-xl p-8 text-center hover:shadow-lg transition-all cursor-pointer group"
            >
              <div className="w-16 h-16 flex items-center justify-center bg-primary/10 rounded-full mx-auto mb-5 group-hover:bg-primary/20 transition-colors">
                <i className="ri-map-pin-2-line text-3xl text-primary"></i>
              </div>
              <h3 className="font-serif text-xl text-navy mb-2">Visit Us</h3>
              <p className="text-navy/70 text-sm leading-relaxed">
                825 Simms St<br />
                Golden, CO 80401
              </p>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
